// Helpers for turning stored post fields into something renderable.
//
// The admin form used to have a single "Excerpt" textarea and no body field, so
// existing posts have their whole article (as HTML) saved in `excerpt` with
// `content` empty. These helpers handle both shapes: a body in `content`
// (HTML or Markdown) and a legacy body in `excerpt`.

import { marked } from 'marked';

const HTML_TAG = /<(p|div|h[1-6]|ul|ol|li|blockquote|figure|img|br|table|section|article|strong|em|a)\b[^>]*>/i;

// A leading run of headings, e.g. the <h1>/<h2> many posts open with.
const LEADING_HEADINGS = /^\s*<h([1-6])\b[^>]*>([\s\S]*?)<\/h\1>\s*/i;

export const looksLikeHtml = (str) => Boolean(str) && HTML_TAG.test(str);

// Flatten markup to readable plain text — for excerpts, read times, meta tags.
export const toPlainText = (str) => {
  if (!str) return '';
  return str
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<\/(p|div|h[1-6]|li|blockquote|tr)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
};

// Admin-authored HTML still shouldn't be able to run scripts once inlined.
export const sanitizeHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<(script|style|iframe|object|embed)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<\/?(script|style|iframe|object|embed|link|meta|base|form)\b[^>]*>/gi, '')
    .replace(/\son\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/\s(href|src|srcset)\s*=\s*(?:"\s*javascript:[^"]*"|'\s*javascript:[^']*'|javascript:[^\s>]+)/gi, '');
};

const normalize = (str) => toPlainText(str).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Legacy bodies repeat the title as an <h1>/<h2>; the page already shows it in
// the hero, so drop up to two leading headings that just restate the title.
const dropRedundantTitleHeadings = (html, title) => {
  const wantedTitle = normalize(title);
  if (!wantedTitle) return html;

  let result = html;
  for (let i = 0; i < 2; i += 1) {
    const match = result.match(LEADING_HEADINGS);
    if (!match) break;
    const heading = normalize(match[2]);
    const isRedundant =
      heading.length >= 6 && (wantedTitle.includes(heading) || heading.includes(wantedTitle));
    if (!isRedundant) break;
    result = result.slice(match[0].length);
  }
  return result;
};

// Full article markup, ready for dangerouslySetInnerHTML.
export const postBodyHtml = (post) => {
  if (!post) return '';
  const raw = (post.content || '').trim() || (post.excerpt || '').trim();
  if (!raw) return '';
  const html = looksLikeHtml(raw) ? raw : marked(raw, { breaks: true, gfm: true });
  return sanitizeHtml(dropRedundantTitleHeadings(html, post.title));
};

// Prefer real prose for summaries: pull the <p> text and skip section headings.
const paragraphText = (html) => {
  const paragraphs = html.match(/<p\b[^>]*>[\s\S]*?<\/p>/gi);
  if (!paragraphs) return null;
  return paragraphs.map(toPlainText).filter(Boolean).join(' ') || null;
};

// Short plain-text summary, cut on a word boundary.
export const makeExcerpt = (str, maxLength = 180) => {
  const raw = String(str || '');
  const text = (looksLikeHtml(raw) && paragraphText(raw)) || toPlainText(raw);
  if (text.length <= maxLength) return text;
  const clipped = text.slice(0, maxLength);
  const lastSpace = clipped.lastIndexOf(' ');
  const cut = lastSpace > maxLength * 0.5 ? lastSpace : maxLength;
  return `${text.slice(0, cut).replace(/[\s,;:.!?-]+$/, '')}…`;
};

export const postExcerpt = (post, maxLength) =>
  makeExcerpt(post?.excerpt || post?.content, maxLength);

// Stored titles carry stray double spaces ("Magic on the Menu Mondays:  Chrystal's"),
// which render literally. Collapse runs of whitespace at display time.
export const postTitle = (title) => String(title || '').replace(/\s+/g, ' ').trim();

export const postReadTime = (post) => {
  const text = toPlainText((post?.content || '').trim() || (post?.excerpt || '').trim());
  if (!text) return null;
  const minutes = Math.max(1, Math.round(text.split(/\s+/).length / 200));
  return `${minutes} min read`;
};
